// shared/pdf/documents/PaymentVoucherDocument.jsx
import React from 'react';
import { Document, Page, View, Text, StyleSheet } from '@react-pdf/renderer';
import { PDF_THEMES, DEFAULT_THEME } from '../themes';
import PdfHeader from '../components/PdfHeader';
import PdfFooter from '../components/PdfFooter';
import PdfSignatureBlock from '../components/PdfSignatureBlock';
import { PdfTableHead, PdfTableRow, PdfTableFoot } from '../components/PdfTable';
import { fmtINR, fmtDate, cap } from '../pdfUtils';

const s = StyleSheet.create({
  page:    { fontFamily: 'NotoSans', paddingBottom: 60 },
  metaRow: { flexDirection: 'row', gap: 10, marginHorizontal: 40, marginTop: 14, marginBottom: 14 },
  metaBox: { flex: 1, padding: 10, borderRadius: 4 },
  metaLbl: { fontSize: 7, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 4 },
  metaVal: { fontSize: 11, fontWeight: 'bold' },
  section: { marginHorizontal: 40, marginBottom: 14 },
  secBar:  { padding: 5, marginBottom: 8, borderRadius: 3 },
  secLbl:  { fontSize: 7.5, fontWeight: 'bold', color: '#FFFFFF', textTransform: 'uppercase', letterSpacing: 0.5 },
  kv:      { flexDirection: 'row', marginBottom: 6 },
  kvLabel: { fontSize: 8, width: 90 },
  kvValue: { fontSize: 9, flex: 1 },
  table:   { marginHorizontal: 40, marginBottom: 14 },
});

const COLS = [
  { label: 'Account',   flex: 3 },
  { label: 'Type',      flex: 1 },
  { label: 'Amount',    flex: 1.5, align: 'right' },
];

export default function PaymentVoucherDocument({ data, theme: themeKey, orientation, sections }) {
  const t = PDF_THEMES[themeKey] || PDF_THEMES[DEFAULT_THEME];
  const { voucher, derasar, journalLines = [] } = data;
  const total = journalLines.filter((l) => l.type === 'debit').reduce((s, l) => s + Number(l.amount || 0), 0);

  return (
    <Document>
      <Page size="A4" orientation={orientation || 'portrait'} style={[s.page, { backgroundColor: t.white }]}>
        {sections.header && <PdfHeader derasar={derasar} reportTitle="Payment Voucher" theme={t} />}

        <View style={s.metaRow}>
          {[
            { label: 'Voucher No.',    value: voucher.voucherNo || '—' },
            { label: 'Date',           value: fmtDate(voucher.voucherDate) },
            { label: 'Financial Year', value: voucher.financialYearId?.yearLabel || '—' },
            { label: 'Mode',           value: cap(voucher.paymentMode) },
          ].map((item, i) => (
            <View key={i} style={[s.metaBox, { backgroundColor: t.light }]}>
              <Text style={[s.metaLbl, { color: t.muted }]}>{item.label}</Text>
              <Text style={[s.metaVal, { color: t.body }]}>{item.value}</Text>
            </View>
          ))}
        </View>

        <View style={s.section}>
          <View style={[s.secBar, { backgroundColor: t.primary }]}>
            <Text style={s.secLbl}>Payment Details</Text>
          </View>
          {[
            voucher.payeeName ? { label: 'Payee',     value: voucher.payeeName } : null,
            voucher.chequeNo  ? { label: 'Cheque',    value: voucher.chequeNo  } : null,
            voucher.narration ? { label: 'Narration', value: voucher.narration } : null,
          ].filter(Boolean).map((kv, i) => (
            <View key={i} style={s.kv}>
              <Text style={[s.kvLabel, { color: t.muted }]}>{kv.label}</Text>
              <Text style={[s.kvValue, { color: t.body }]}>{kv.value}</Text>
            </View>
          ))}
        </View>

        <View style={s.table}>
          <PdfTableHead cols={COLS} theme={t} />
          {journalLines.map((line, i) => (
            <PdfTableRow
              key={i}
              cols={COLS}
              values={[line.accountName || '—', cap(line.type), fmtINR(line.amount)]}
              index={i}
              theme={t}
            />
          ))}
          <PdfTableFoot cols={COLS} values={['Total', '', fmtINR(total)]} theme={t} />
        </View>

        {sections.signature && <PdfSignatureBlock theme={t} />}
        {sections.footer    && <PdfFooter theme={t} />}
      </Page>
    </Document>
  );
}


